import { create } from 'zustand'

/**
 * Panel-mode signals the workspace mosaic chrome reads to frame the ENTIRE
 * panel component (the mosaic window), not just the chart body inside it.
 *
 * `drillOpen` is keyed by the OUTER workspace panel id (the same id the mosaic
 * renders the panel under). A chart host sets it true while its drill overlay
 * is open and clears it on close / unmount; the mosaic reads it to add the
 * purple `mosaic-window--drill` border around the whole window. No persistence
 * — this is transient view state that resets on reload.
 */
interface PanelModeState {
  drillOpen: Record<string, boolean>
  setDrillOpen: (panelId: string, open: boolean) => void
  /**
   * `replayOpen` mirrors `drillOpen` exactly — keyed by the OUTER workspace
   * panel id, set true while a chart's bar replay is active. The mosaic reads it
   * to add the orange `mosaic-window--replay` border around the whole window.
   * Transient (no persistence): a reloaded page comes back live, not replaying.
   */
  replayOpen: Record<string, boolean>
  setReplayOpen: (panelId: string, open: boolean) => void
  /**
   * "Raise the header calendar in replay mode" is the one signal that has to
   * travel from a panel BODY (the ⋯-menu action, the chart's right-click menu)
   * to the panel HEADER, which the mosaic renders in a different tree. The
   * reverse direction (start/exit replay) rides the existing registered
   * toolbar-action handler, so it needs no store state.
   *
   * Spec: docs/superpowers/specs/2026-08-27-chart-replay-calendar-design.md
   */
  replayRequest: Record<string, ReplayPickerRequest | undefined>
  requestReplayPicker: (
    panelId: string,
    opts?: { date?: string | null; cellId?: string | null },
  ) => void
  clearReplayRequest: (panelId: string) => void
}

/** One "open the calendar for replay" request. */
export interface ReplayPickerRequest {
  /** Day to pre-stage, or null to fall back to the panel's current day. */
  date: string | null
  /** Chart cell to replay (grid cells register as `panelId::i`), or null for the panel's own chart. */
  cellId: string | null
  /** Bumped per request so asking twice for the SAME day still reopens the picker. */
  nonce: number
}

export const usePanelMode = create<PanelModeState>((set) => ({
  drillOpen: {},
  setDrillOpen: (panelId, open) =>
    set((s) => {
      if (!!s.drillOpen[panelId] === open) return s
      const next = { ...s.drillOpen }
      if (open) next[panelId] = true
      else delete next[panelId]
      return { drillOpen: next }
    }),
  replayOpen: {},
  setReplayOpen: (panelId, open) =>
    set((s) => {
      if (!!s.replayOpen[panelId] === open) return s
      const next = { ...s.replayOpen }
      if (open) next[panelId] = true
      else delete next[panelId]
      return { replayOpen: next }
    }),
  replayRequest: {},
  requestReplayPicker: (panelId, opts) =>
    set((s) => ({
      replayRequest: {
        ...s.replayRequest,
        [panelId]: {
          date: opts?.date ?? null,
          cellId: opts?.cellId ?? null,
          nonce: (s.replayRequest[panelId]?.nonce ?? 0) + 1,
        },
      },
    })),
  clearReplayRequest: (panelId) =>
    set((s) => {
      if (!s.replayRequest[panelId]) return s
      const next = { ...s.replayRequest }
      delete next[panelId]
      return { replayRequest: next }
    }),
}))
